/**
 * OIDC PKCE flow against Keycloak — single source of truth for the Chronos SPA.
 *
 * Security:
 * - Tokens are persisted in sessionStorage only (never localStorage).
 * - Authorization Code + PKCE (default in oidc-client-ts via response_type='code').
 * - Silent renew uses a dedicated iframe redirect_uri.
 * - Never logs token material or the full User object.
 */
import {
  UserManager,
  WebStorageStateStore,
  Log as OidcLog,
  type User,
} from "oidc-client-ts";
import { env } from "../env";

const RETURN_URL_KEY = "workforce.returnUrl";
const LOGGED_OUT_KEY = "workforce.loggedOut";

/**
 * True une seule fois après un logout volontaire (lu-puis-effacé). Permet à
 * AuthProvider de NE PAS relancer signinSilent au retour sur "/", sinon le cookie
 * SSO Keycloak survivant reconnecterait l'utilisateur immédiatement.
 */
export function consumeLoggedOutFlag(): boolean {
  const flagged = sessionStorage.getItem(LOGGED_OUT_KEY) === "1";
  sessionStorage.removeItem(LOGGED_OUT_KEY);
  return flagged;
}

const issuer = `${env.VITE_KEYCLOAK_URL.replace(/\/$/, "")}/realms/${env.VITE_KEYCLOAK_REALM}`;
const appBase = env.VITE_APP_BASE_URL.replace(/\/$/, "");

export const userManager = new UserManager({
  authority: issuer,
  client_id: env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: `${appBase}/auth/callback`,
  silent_redirect_uri: `${appBase}/auth/silent`,
  post_logout_redirect_uri: `${appBase}/`,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: true,
  automaticSilentRenew: true,
  monitorSession: false,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  filterProtocolClaims: true,
});

if (import.meta.env.DEV) {
  OidcLog.setLogger(console);
  OidcLog.setLevel(OidcLog.WARN);
}

// Renouvellement OIDC mutualisé : les 4 clients API (tawa/lte/tasks/pm) peuvent
// demander un token quasi simultanément (ex. Home monte LTE+TM+PM en parallèle,
// token expiré, ou 401 en rafale). Un seul signinSilent() en vol à la fois évite
// les renouvellements concurrents qui se supplantent et font échouer une partie
// des requêtes. TOUT renouvellement silencieux doit passer par ici.
let renewInFlight: Promise<User | null> | null = null;
export function renewSession(): Promise<User | null> {
  if (!renewInFlight) {
    renewInFlight = userManager.signinSilent().finally(() => {
      renewInFlight = null;
    });
  }
  return renewInFlight;
}

userManager.events.addAccessTokenExpiring(() => {
  void renewSession().catch((err: unknown) => {
    console.warn("[oidc] silent renew on expiring failed", err);
  });
});

userManager.events.addSilentRenewError((err: Error) => {
  console.error("[oidc] silent renew error", err);
});

userManager.events.addUserSignedOut(() => {
  sessionStorage.removeItem(RETURN_URL_KEY);
});

export async function login(returnUrl?: string): Promise<void> {
  sessionStorage.setItem(RETURN_URL_KEY, returnUrl ?? "/");
  await userManager.signinRedirect();
}

export async function logout(): Promise<void> {
  // Marqueur consommé par AuthProvider au retour (cf. consumeLoggedOutFlag).
  sessionStorage.setItem(LOGGED_OUT_KEY, "1");
  const user = await userManager.getUser();
  const id_token_hint = user?.id_token;

  // Navigation top-level DIRECTE vers l'end_session_endpoint. Deux raisons :
  // 1. On évite signoutRedirect : son fetch de metadata OIDC est tué par l'ETP
  //    stricte de Firefox/Zen (NetworkError) → throw.
  // 2. On ne touche PAS à l'état auth local avant de partir. Un removeUser()
  //    basculerait le statut en "unauthenticated", ce qui ferait déclencher à
  //    ProtectedRoute un auto-login (signinRedirect) vers la session SSO encore
  //    vivante → course qui reconnecte l'utilisateur (le vrai bug). Le statut
  //    reste "initializing" jusqu'au unload. Le user local est nettoyé au retour
  //    (branche justLoggedOut de AuthProvider).
  const params = new URLSearchParams({
    post_logout_redirect_uri: `${appBase}/`,
  });
  if (id_token_hint) params.set("id_token_hint", id_token_hint);
  window.location.assign(
    `${issuer}/protocol/openid-connect/logout?${params.toString()}`,
  );
}

let callbackInFlight: Promise<{ user: User; returnUrl: string }> | null = null;

export async function handleCallback(): Promise<{
  user: User;
  returnUrl: string;
}> {
  if (callbackInFlight) return callbackInFlight;
  callbackInFlight = (async () => {
    const user = await userManager.signinRedirectCallback();
    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) ?? "/";
    sessionStorage.removeItem(RETURN_URL_KEY);
    return { user, returnUrl };
  })();
  return callbackInFlight;
}

export async function handleSilentRenew(): Promise<void> {
  await userManager.signinSilentCallback();
}

export async function getUser(): Promise<User | null> {
  return userManager.getUser();
}

export async function getAccessToken(): Promise<string | null> {
  const user = await userManager.getUser();
  if (user && !user.expired) {
    return user.access_token;
  }
  try {
    const renewed = await renewSession();
    return renewed?.access_token ?? null;
  } catch (err: unknown) {
    console.warn("[oidc] silent renew failed", err);
    return null;
  }
}

export function isAuthenticated(user: User | null): boolean {
  return user !== null && !user.expired;
}
